// ─────────────────────────────────────────────────────────────────────────────
// Regla #3 — Enrutamiento del Home. Índice de /app.
//
// RF-011 (§25): el ruteo es por los FLAGS del PERFIL (no por campaña activa):
//   · canViewPulso && canViewBrillamos → LandingChooser (el usuario elige Pulso o Shine).
//   · sin Pulso (solo Brillamos, o Frontline) → Reconocimiento (/app/brillamos si la campaña
//     Brillamos está activa, si no /app/reconocer — default de Fundamentales).
//   · solo Pulso (sin Brillamos) que aterrizó aquí → portal Pulso (`/`, otro armazón).
// Frontline NUNCA tiene Pulso (RN-A06) y siempre es usuario de Brillamos.
// Cargando (perfil/OU/campaña) → spinner honesto; perfil no resuelto a tiempo → Reconocimiento.
//
// NO toca la lógica de auth: se apoya en la sesión Frontline / MSAL ya resueltas por AuthProvider.
//
// Bug Sesión #98 — este mismo componente se reutiliza en el comodín `/app/*` (ver AppRouter): antes
// una URL "rara" bajo /app (mal escrita, enlace viejo) iba SIEMPRE a /app/reconocer sin condición,
// sin importar si Brillamos estaba activa. Reusar `LandingDecision` en vez de un destino fijo hace
// que el acceso por URL respete la MISMA regla que la portada, y evita que las dos rutas diverjan.
//
// Bug Sesión #98 (extendido) — el mismo hueco seguía abierto en las rutas FIJAS /app/reconocer y
// /app/brillamos: un bookmark o el tab de Teams apuntando directo a una de las dos se saltaba
// esta pantalla por completo. `recoTarget`/`isLoading` se extrajeron a `useRecoHomeTarget` (única
// fuente) para que `AppLayout` pueda aplicar la MISMA decisión, una sola vez al entrar, sin volver
// a evaluarla si el usuario navega después por el sidebar — ver el comentario en `AppLayout.tsx`.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasFrontlineSession } from '@/auth/frontlineSession';
import { useIsSessionExpired } from '@/auth/sessionExpiredBus';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useRecoHomeTarget } from '@/hooks/useRecoHomeTarget';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UserProvider } from '@/contexts/UserContext';
import { LandingChooser } from './LandingChooser';
import '../recognition.css';
import { withBase } from '@/utils/basePath';

function LandingSpinner() {
  const { t } = useTranslation('recognition');
  return (
    <div className="acc-loading" role="status">
      <i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('common.loading', 'Cargando…')}
    </div>
  );
}

function LandingDecisionInner() {
  const isFrontline = hasFrontlineSession();
  const currentUser = useCurrentUser();

  const { recoTarget, isLoading: recoLoading } = useRecoHomeTarget();

  // RF-011 — los flags solo son fiables cuando el perfil resolvió: `modules` arranca en su fallback
  // "solo Pulso" durante la carga, así que leerlos antes botaría a cualquier usuario Sigma a Pulso.
  const profileReady = isFrontline || (currentUser?.profileResolved ?? false);
  // Frontline NUNCA tiene Pulso (RN-A06) y siempre es usuario de Brillamos.
  const canViewPulso = !isFrontline && profileReady && (currentUser?.modules.canViewPulso ?? false);
  const canViewBrillamos = isFrontline || (profileReady && (currentUser?.modules.canViewBrillamos ?? false));

  // Un usuario SOLO-Pulso (sin Brillamos) que llegó al armazón de Reconocimiento: su lugar es el
  // portal Pulso (otro armazón → navegación de página completa, en efecto). SOLO con el perfil ya
  // resuelto: nunca durante la carga (si no, el fallback "solo Pulso" lo dispararía siempre).
  // Tampoco con la sesión expirada: `getCurrentUserProfile` devuelve el mismo perfil de emergencia
  // "solo Pulso" cuando el fetch falla por sesión caída (no solo cuando el usuario genuinamente no
  // tiene Brillamos), y sin este guard eso expulsaba de Reconocimiento hacia Pulso sin aviso cada
  // vez que la sesión expiraba a mitad de uso (bug reportado 2026-08-31).
  const isSessionExpired = useIsSessionExpired();
  const goToPulsoPortal = !isFrontline && profileReady && !isSessionExpired
    && canViewPulso && !canViewBrillamos;
  useEffect(() => {
    if (goToPulsoPortal) window.location.replace(withBase('/'));
  }, [goToPulsoPortal]);

  if (recoLoading) return <LandingSpinner />;

  if (goToPulsoPortal) return <LandingSpinner />;   // redirigiendo al portal Pulso
  if (canViewPulso && canViewBrillamos) return <LandingChooser recoTarget={recoTarget} />;
  // Sin Pulso (solo Brillamos / Frontline), o perfil no resuelto a tiempo → Reconocimiento.
  return <Navigate to={recoTarget} replace />;
}

export function LandingDecision() {
  return (
    <UserProvider>
      <ThemeProvider>
        <main className="acc-standalone">
          <LandingDecisionInner />
        </main>
      </ThemeProvider>
    </UserProvider>
  );
}

export default LandingDecision;
